import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  Check,
  MessageCircle,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  StickyNote,
  Trash2,
} from 'lucide-react';
import {
  ACTIVITY_TYPE_LABELS,
  APPOINTMENT_OUTCOME_LABELS,
  CALL_OUTCOME_LABELS,
  type Activity,
  type ActivitySubject,
  type ActivityType,
} from '@aimmo/contracts';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  useConfirm,
} from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';
import { useAbility } from '../lib/abilities.js';
import { completeActivity, isEditableActivity, removeActivity } from '../lib/activities.js';
import { ActivityDialog } from './activity-dialog.js';
import { formatDateTime } from '../lib/format.js';

const ICONS: Record<ActivityType, typeof Phone> = {
  call: Phone,
  appointment: CalendarClock,
  reminder: CalendarClock,
  note: StickyNote,
  status_change: RefreshCw,
  // Written by the inbound handler, never by a person (M8): a conversation
  // shows on the timeline next to the calls it competes with.
  whatsapp: MessageCircle,
};

export function activityKeys(subjectType: ActivitySubject, subjectId: string) {
  return ['activities', 'timeline', subjectType, subjectId] as const;
}

function outcomeLabel(activity: Activity): string | null {
  if (!activity.outcome) return null;
  const table: Record<string, string> = {
    ...CALL_OUTCOME_LABELS,
    ...APPOINTMENT_OUTCOME_LABELS,
  };
  return table[activity.outcome] ?? activity.outcome;
}

/**
 * The unified timeline (ADR-0006): everything that ever happened about one
 * contact, property, mandate or demand, newest first, plus the box to add the
 * next entry.
 *
 * Five legacy node types collapsed into this one list — which is the whole
 * reason the single table was worth the weaker per-type constraints.
 */
export function ActivityTimeline({
  subjectType,
  subjectId,
  title = 'Attività',
}: {
  subjectType: ActivitySubject;
  subjectId: string;
  title?: string;
}) {
  const queryClient = useQueryClient();
  const ability = useAbility();
  const canUpdate = ability.can('update', 'Activity');
  const canDelete = ability.can('delete', 'Activity');
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Activity | null>(null);
  const { confirm, dialog } = useConfirm();

  const { data, isPending } = useQuery({
    queryKey: activityKeys(subjectType, subjectId),
    queryFn: async ({ signal }) =>
      assertOk(
        await api.activities.list({
          query: {
            page: 1,
            pageSize: 50,
            order: 'desc',
            sort: 'createdAt',
            subjectType,
            subjectId,
          },
          fetchOptions: { signal },
        }),
      ).body,
  });

  const complete = useMutation({
    mutationFn: (id: string) => completeActivity(id, queryClient),
  });

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle>{title}</CardTitle>

        {/* One dialog for every kind of activity (U5): the subject is fixed
            here, so the picker it carries for the Agenda stays hidden. */}
        <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(true)}>
          <Plus />
          Aggiungi
        </Button>
        <ActivityDialog
          open={open}
          onOpenChange={setOpen}
          subject={{ type: subjectType, id: subjectId }}
        />
        {editing && (
          <ActivityDialog
            open
            activity={editing}
            onOpenChange={(next) => {
              if (!next) setEditing(null);
            }}
          />
        )}
        {dialog}
      </CardHeader>
      <CardContent className="grid gap-4">
        {isPending && <Skeleton className="h-20 w-full" />}

        {!isPending && (data?.items.length ?? 0) === 0 && (
          <p className="text-secondary text-muted">Nessuna attività registrata.</p>
        )}

        <ol className="grid gap-2">
          {data?.items.map((activity) => {
            const Icon = ICONS[activity.type];
            const outcome = outcomeLabel(activity);

            return (
              <li
                key={activity.id}
                className="grid grid-cols-[auto_1fr_auto] items-start gap-3 rounded-control border border-border px-3 py-2"
              >
                <Icon className="mt-0.5 size-4 text-muted" />

                <div className="grid gap-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-body text-ink">
                      {activity.title ?? ACTIVITY_TYPE_LABELS[activity.type]}
                    </span>
                    {activity.appointmentTypeLabel && (
                      <span
                        className="rounded-pill px-2 py-0.5 text-label uppercase tracking-[0.08em]"
                        style={{
                          backgroundColor: activity.appointmentTypeColor ?? undefined,
                          color: activity.appointmentTypeTextColor ?? undefined,
                        }}
                      >
                        {activity.appointmentTypeLabel}
                      </span>
                    )}
                    {outcome && <Badge tone="neutral">{outcome}</Badge>}
                    {activity.completedAt && <Badge tone="success">Fatto</Badge>}
                  </div>

                  {activity.body && (
                    <p className="text-secondary text-ink-2 whitespace-pre-line">{activity.body}</p>
                  )}

                  <p className="text-secondary text-muted">
                    {[
                      activity.scheduledAt
                        ? `Previsto ${formatDateTime(activity.scheduledAt)}`
                        : formatDateTime(activity.createdAt),
                      activity.userName,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>

                <div className="flex items-center gap-0.5">
                  {!activity.completedAt && activity.type !== 'status_change' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Segna come fatto"
                      disabled={complete.isPending}
                      onClick={() => complete.mutate(activity.id)}
                    >
                      <Check />
                    </Button>
                  )}
                  {/* The audit trail is not editable history: the API refuses
                      both on a status change, so neither is offered. */}
                  {canUpdate && isEditableActivity(activity) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Modifica attività"
                      onClick={() => setEditing(activity)}
                    >
                      <Pencil />
                    </Button>
                  )}
                  {canDelete && isEditableActivity(activity) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Elimina attività"
                      onClick={() =>
                        confirm(
                          {
                            title: 'Eliminare questa attività?',
                            description: `${activity.title ?? ACTIVITY_TYPE_LABELS[activity.type]}: sparisce dalla cronologia, dall’agenda e dai promemoria. L’operazione non si annulla.`,
                            confirmLabel: 'Elimina',
                          },
                          'danger',
                          () => removeActivity(activity.id, queryClient),
                        )
                      }
                    >
                      <Trash2 />
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}
